/** Formatting + presentation helpers shared across chat components. */

import type { MachineSummary, PartSuggestion } from "./types";

/** A set of suggestions that all address the same requested part. */
export interface SuggestionGroup {
  requestedItem: string;
  suggestions: PartSuggestion[];
}

/**
 * Group a flat suggestions list by `requested_item`, preserving first-seen order
 * of both the groups and the suggestions within each group. Robust to the array
 * being ordered or interleaved across requests.
 */
export function groupSuggestions(suggestions: PartSuggestion[]): SuggestionGroup[] {
  const order: string[] = [];
  const byItem = new Map<string, PartSuggestion[]>();

  for (const suggestion of suggestions) {
    const key = suggestion.requested_item;
    let bucket = byItem.get(key);
    if (!bucket) {
      bucket = [];
      byItem.set(key, bucket);
      order.push(key);
    }
    bucket.push(suggestion);
  }

  return order.map((requestedItem) => ({
    requestedItem,
    suggestions: byItem.get(requestedItem) ?? [],
  }));
}

/** Compact relative time like "just now", "5m ago", "3h ago", "2d ago". */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 45) {
    return "just now";
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  return new Date(iso).toLocaleDateString();
}

export type ConfidenceTier = "high" | "medium" | "low";

/** Bucket a 0.0-1.0 confidence score into a tier for badge/bar coloring. */
export function confidenceTier(confidence: number): ConfidenceTier {
  if (confidence >= 0.75) {
    return "high";
  }
  if (confidence >= 0.45) {
    return "medium";
  }
  return "low";
}

/** Whole-percent string for a 0.0-1.0 confidence score. */
export function confidencePercent(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Normalise an optional API string to `string | null`.
 *
 * The nullable suggestion fields (`description`, the vendor triple) can arrive
 * as `null`, missing entirely on suggestions persisted before they existed, or
 * as whitespace-only strings from the source BOM rows. Collapse all three into
 * `null` so callers can render on a single truthiness check.
 */
export function presentText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Lines shown before a part description collapses behind a "Show more" toggle.
 * Kept in sync by hand with the `line-clamp-3` class in `suggestion-card.tsx`
 * (Tailwind can't take a dynamic clamp value).
 */
const DESCRIPTION_CLAMP_LINES = 3;

/** Characters past which a single-paragraph description is treated as long. */
const DESCRIPTION_CLAMP_CHARS = 180;

/**
 * Whether a description needs a "Show more" toggle rather than rendering in
 * full. Descriptions range from a short one-liner to a ~10-line technical spec
 * block, so check both wrapped length and hard line count.
 */
export function isLongDescription(description: string): boolean {
  return (
    description.length > DESCRIPTION_CLAMP_CHARS ||
    description.split("\n").length > DESCRIPTION_CLAMP_LINES
  );
}

/** Whether a content type / file name looks like a displayable image. */
export function isImageAttachment(contentType: string | null, fileName: string): boolean {
  if (contentType?.startsWith("image/")) {
    return true;
  }
  return /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i.test(fileName);
}

/**
 * The machine number on a DISA machine's plate: the trailing `-<digits>` of its
 * item id. DISA's convention only — another brand's `T50R-0001` ends in digits
 * that are no plate number.
 */
const MACHINE_SERIAL = /-(\d{4,8})$/;
/** Exact, case-sensitive — matches `_SERIAL_BRAND` in the Teams card. */
const SERIAL_BRAND = "DISA";

/** Join the non-empty parts of a label with the picker's separator. */
function joinLabel(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(" · ") || "?";
}

/**
 * The type designation, else the model name. `machine_type` leads because the
 * BOM export's `model_name` is 'A_DMM' or 'A_231D DMM' across the whole DISA
 * fleet; Wheelabrator machines have no type, so it falls back.
 */
function machineName(machine: MachineSummary): string {
  return machine.machine_type || machine.model_name || "";
}

/** The plate number from a DISA item id's trailing `-<digits>`, else `null`. */
function machineSerial(machine: MachineSummary): string | null {
  if (machine.brand !== SERIAL_BRAND) {
    return null;
  }
  return MACHINE_SERIAL.exec(machine.item_id)?.[1] ?? null;
}

/**
 * Type plus machine number for DISA, type plus item id elsewhere — the type
 * alone repeats across every DISA 280.
 */
function machineLabel(machine: MachineSummary): string {
  const serial = machineSerial(machine);
  return joinLabel(machineName(machine), serial ? `No. ${serial}` : machine.item_id);
}

/** The full item id standing in for the machine number. */
function machineLabelByItemId(machine: MachineSummary): string {
  return joinLabel(machineName(machine), machine.item_id);
}

/** The item-id label plus project and model, for two revisions of one row. */
function machineLabelInFull(machine: MachineSummary): string {
  const { project_number: project, model_name: model } = machine;
  return joinLabel(
    machineLabelByItemId(machine),
    project && `project ${project}`,
    model && model !== machineName(machine) ? model : null,
  );
}

/** A picker option: the machine plus a headline no other option shares. */
export interface MachineOption {
  machine: MachineSummary;
  label: string;
}

/**
 * Drop a repeated row, and any machine identical in every field the picker can
 * show — two such options are two buttons nobody can choose between. The
 * backend already does this (`picker_summaries`); this covers a stored payload
 * from before it did. Mirrors `_distinct_machines` in the Teams card.
 */
function distinctMachines(machines: MachineSummary[]): MachineSummary[] {
  const seenIds = new Set<number>();
  const seenIdentities = new Set<string>();
  return machines.filter((machine) => {
    const identity = JSON.stringify([
      machine.item_id,
      machine.machine_type ?? null,
      machine.model_name,
      machine.project_number,
    ]);
    if (seenIds.has(machine.id) || seenIdentities.has(identity)) {
      return false;
    }
    seenIds.add(machine.id);
    seenIdentities.add(identity);
    return true;
  });
}

/**
 * One picker option per distinct machine, each headline different from every
 * other. Starts short and lengthens only the labels that still collide. Mirrors
 * `_machine_labels` in `backend/.../copilot/translate.py` so the web picker and
 * the Teams one name a machine the same way.
 */
export function machineOptions(machines: MachineSummary[]): MachineOption[] {
  let options = distinctMachines(machines).map((machine) => ({
    machine,
    label: machineLabel(machine),
  }));
  for (const fuller of [machineLabelByItemId, machineLabelInFull]) {
    const counts = new Map<string, number>();
    options.forEach(({ label }) => counts.set(label, (counts.get(label) ?? 0) + 1));
    options = options.map(({ machine, label }) => ({
      machine,
      label: (counts.get(label) ?? 0) > 1 ? fuller(machine) : label,
    }));
  }
  return options;
}

/**
 * Whether the row was created from a parts catalogue rather than a BOM export —
 * keyed `<machine type or brand>-<machine number>` instead of an ERP id. Such a
 * machine's parts come back with no spare/wear flag and no vendor.
 */
function isCatalogueOnly(machine: MachineSummary): boolean {
  const prefix = machine.machine_type || machine.brand;
  const serial = MACHINE_SERIAL.exec(machine.item_id);
  return Boolean(prefix && serial) && machine.item_id === `${prefix}${serial?.[0]}`;
}

/** Lower-cased alphanumeric runs: what counts as one word in a picker label. */
function labelTokens(text: string): string[] {
  return text.toLowerCase().match(/[a-z0-9]+/g) ?? [];
}

/**
 * Whether `value`'s words appear in `label` as a run of whole words, so a DISA
 * project equal to its serial is shown by "No. 150330" but project `1503` is
 * not. Mirrors `_shown_in` in the Teams card.
 */
function shownIn(value: string, label: string): boolean {
  const needle = labelTokens(value);
  const haystack = labelTokens(label);
  if (needle.length === 0) {
    return false;
  }
  for (let start = 0; start + needle.length <= haystack.length; start++) {
    if (needle.every((token, offset) => haystack[start + offset] === token)) {
      return true;
    }
  }
  return false;
}

/** What the picker says about one option beyond its headline. */
export function machineDetails(machine: MachineSummary, label: string): string {
  const { item_id: itemId, project_number: project, model_name: model } = machine;
  const details = [
    itemId && !shownIn(itemId, label) ? `item ${itemId}` : null,
    project && !shownIn(project, label) ? `project ${project}` : null,
    model && !shownIn(model, label) ? `model ${model}` : null,
    isCatalogueOnly(machine) ? "parts catalogue only, no BOM export" : null,
  ];
  return details.filter(Boolean).join(" · ") || machine.brand;
}

/**
 * The vendor triple as one line: name, country in brackets, id last. `null`
 * when none of the three is on record. Matches `_vendor` in the Teams card.
 */
export function vendorText(suggestion: PartSuggestion): string | null {
  const name = presentText(suggestion.vendor_name);
  const country = presentText(suggestion.vendor_country);
  const id = presentText(suggestion.vendor_id);
  let head = name ?? id ?? "";
  if (country) {
    head = head ? `${head} (${country})` : country;
  }
  if (id && name) {
    head = `${head} · ${id}`;
  }
  return head || null;
}
